import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import { setCreativeDetailsModal } from '../creativesSlice';
import { useCdnUrl } from '@/shared/utils';
import { useAppDispatch, useAppSelector } from '@/app';
import {
  CREATIVE_TYPE,
  isUrlCreative,
  normalizeCreativeType,
} from '@/shared/constants/creativeTypes';

const CreativeDetailsModal = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { open, selectedCreative: creative } = useAppSelector(
    state => state.dooh.creative.creativeDetailsModal
  );

  const onClose = () =>
    dispatch(setCreativeDetailsModal({ open: false, selectedCreative: null }));

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  const normalizedFiles =
    Array.isArray(creative?.files) && creative.files.length > 0
      ? creative.files
      : creative?.src?.files
        ? Object.values(creative.src.files)
        : [];

  useEffect(() => {
    if (Array.isArray(creative?.files) && normalizedFiles.length > 0) {
      setSelectedIndex(0);
    }
    setCopied(false);
  }, [creative]);

  //  CHECK IF IT'S A VAST/WEB CREATIVE
  const isUrlCreativeType = isUrlCreative(creative?.type);

  const urlFromSrc =
    creative?.url ||
    creative?.src?.url ||
    (typeof creative?.src === 'string' ? creative.src : null);

  //  SHOW SIMPLIFIED MODAL FOR VAST/WEB
  if (isUrlCreativeType) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ mb: 3, color: theme.palette.text.primary }}
          >
            {normalizeCreativeType(creative.type) === CREATIVE_TYPE.VAST
              ? 'VAST'
              : 'WEB'}{' '}
            Creative Details
          </Typography>

          <Box
            sx={{
              p: 2,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[900]
                  : theme.palette.grey[100],
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              mb: 3,
              textAlign: 'left',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Uploaded URL:
            </Typography>
            {urlFromSrc ? (
              <Typography
                variant="body1"
                sx={{
                  wordBreak: 'break-all',
                  color: theme.palette.primary.main,
                }}
              >
                {urlFromSrc}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                fontStyle="italic"
              >
                URL not available
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            {urlFromSrc && (
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={() => {
                  navigator.clipboard.writeText(urlFromSrc);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                sx={{
                  borderColor: theme.palette.info.main,
                  color: theme.palette.info.main,
                  '&:hover': {
                    borderColor: theme.palette.info.dark,
                    backgroundColor: theme.palette.info.main,
                    color: theme.palette.info.contrastText,
                  },
                }}
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={onClose}
              sx={{ minWidth: 120 }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    );
  }

  //  DON'T RETURN NULL FOR URL CREATIVES
  const hasMedia =
    (Array.isArray(creative?.files) && normalizedFiles.length > 0) ||
    (creative?.src?.files && Object.keys(creative.src.files).length > 0);

  if (!isUrlCreative && !hasMedia) return null;

  if (!Array.isArray(creative?.files) || normalizedFiles.length === 0) {
    return null;
  }

  const selectedData = normalizedFiles[selectedIndex];
  const fileFormat =
    selectedData?.src?.split('.').pop()?.toUpperCase() || 'N/A';
  const fileType = selectedData?.type || 'N/A';
  const isVideo = ['MP4', 'WEBM', 'MOV'].includes(fileFormat);

  // Use CDN URL for optimized delivery
  const baseUrl = selectedData?.src;
  const cdnUrl = useCdnUrl(baseUrl);

  const handleCopy = () => {
    const textToCopy = isUrlCreativeType ? urlFromSrc : cdnUrl;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleViewMedia = () => {
    if (cdnUrl) {
      window.open(cdnUrl, '_blank');
    }
  };

  const handleDownloadMedia = async () => {
    try {
      const url = baseUrl;
      // Fetch the file (any type)
      const response = await fetch(url);
      const blob = await response.blob();

      // Try to guess a filename from the URL or use a fallback
      const urlParts = url.split('/');
      const rawName = urlParts[urlParts.length - 1]; // last part of the URL
      const cleanName = decodeURIComponent(rawName.split('?')[0]); // remove query params

      const link = document.createElement('a');
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      link.download = cleanName || 'downloaded-file'; // fallback name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Creative Details
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            borderRadius: '0.5rem',
            color: theme.palette.primary.main,
            px: 1.5,
            gap: 1,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }}
        >
          <CloseIcon fontSize="small" />
          <Typography fontSize="0.9rem">Close</Typography>
        </IconButton>
      </Box>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Left: Thumbnails + Media */}
          <Box
            sx={{
              width: '60%',
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[900]
                  : theme.palette.grey[50],
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* Thumbnails */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {creative.files.map((item: any, idx: number) => {
                const format = item.src.split('.').pop().toUpperCase();
                const videoLike = ['MP4', 'WEBM', 'MOV'].includes(format);
                const thumbnailCdnUrl = useCdnUrl(item.src);
                return (
                  <Box
                    key={idx}
                    sx={{
                      width: 80,
                      height: 60,
                      borderRadius: 1,
                      border:
                        selectedIndex === idx
                          ? `2px solid ${theme.palette.primary.main}`
                          : `1px solid ${theme.palette.divider}`,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        opacity: 0.8,
                      },
                    }}
                    onClick={() => setSelectedIndex(idx)}
                  >
                    {videoLike ? (
                      <video
                        src={thumbnailCdnUrl || item.src}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <img
                        src={thumbnailCdnUrl || item.src}
                        alt={`media-${idx}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Main Preview */}
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                height: '400px',
                margin: 'auto',
                backgroundColor: theme.palette.background.paper,
              }}
            >
              {isVideo ? (
                <video
                  src={cdnUrl || selectedData.src}
                  controls
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <img
                  src={cdnUrl || selectedData.src}
                  alt={creative.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              )}
            </Box>

            {/* Media Actions */}
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography fontWeight={600} sx={{ mb: 2 }}>
                Media Actions
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={handleViewMedia}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    flex: 1,
                    minWidth: '120px',
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                    },
                  }}
                >
                  View Media
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadMedia}
                  sx={{
                    borderColor: theme.palette.success.main,
                    color: theme.palette.success.main,
                    flex: 1,
                    minWidth: '120px',
                    '&:hover': {
                      borderColor: theme.palette.success.dark,
                      backgroundColor: theme.palette.success.main,
                      color: theme.palette.success.contrastText,
                    },
                  }}
                >
                  Download Media
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={handleCopy}
                  sx={{
                    borderColor: theme.palette.info.main,
                    color: theme.palette.info.main,
                    flex: 1,
                    minWidth: '120px',
                    '&:hover': {
                      borderColor: theme.palette.info.dark,
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.info.contrastText,
                    },
                  }}
                >
                  {copied ? 'Copied!' : 'Copy Link'}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Right: Accordions */}
          <Box
            sx={{
              width: '40%',
              '& .MuiAccordion-root': {
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
              },
              '& .MuiAccordionSummary-root': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[100],
                '&.Mui-expanded': {
                  minHeight: 48,
                },
              },
            }}
          >
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Creative Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  {creative.status && (
                    <Chip
                      label={creative.status}
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>

                {[
                  { label: 'Creative Name', value: creative.name },
                  { label: 'Duration', value: creative.duration },
                  {
                    label: 'Resolution',
                    value: `${selectedData?.resolutionWidth || 'N/A'}x${selectedData?.resolutionHeight || 'N/A'}`,
                  },
                  {
                    label: 'Aspect Ratio',
                    value: selectedData?.aspectRatio || 'N/A',
                  },
                  {
                    label: 'Creative File Size',
                    value: selectedData?.size || 'N/A',
                  },
                  { label: 'Creative File Type', value: fileType },
                  { label: 'Creative File Format', value: fileFormat },
                  {
                    label: 'Last Updated At',
                    value: creative.updatedAt || 'N/A',
                  },
                ].map((row, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography fontWeight={500}>{row.label}</Typography>
                    <Typography>{row.value}</Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreativeDetailsModal;
